import { useContext, useEffect, useMemo, useState } from 'react';
import { tokens, Stack, Badge, Loader, Text } from '@hotjar/hotsauce';

import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { SCALE } from '@hotjar/webapp/domains/revenue/plan/feature-group';

import { mapSiteAccountModelToDto } from '@hotjar/account';
import { useProductAllowances } from '@hotjar/webapp/domains/revenue/hooks/usePlanAllowances';

import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTranslation } from '@hotjar/translations';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { logError } from '@hotjar/insights-common/lib/logger';

import type {
  AnalyticsProduct,
  FeedbackProduct,
  Product,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  extractSkuFromPlanId,
  getProductBySku,
  getProductSkusByPlanId,
} from '@hotjar/webapp/domains/common/product/utils';

import { SkuDropdown } from '@hotjar/webapp/features/Billing/MultiSkuDropdown/SkuDropdown';
import {
  PlanComparisonColumnsContext,
  getSalesLinkByReferrer,
  productIncludesSku,
} from '@hotjar/webapp/features/Billing/PlanComparisonColumns/utils';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { isTrafficRecommendationReferrer } from '@hotjar/webapp/domains/revenue/upsell/utils';

import type { BillingPeriod } from '@hotjar/subscription-management';
import { PlanPrice } from '@hotjar/webapp/features/Billing/Upsell/components/PlanPrice/PlanPrice';
import { useCanSelfServeUpsell } from '@hotjar/upsell';
import type { LegacyPlanId } from '@hotjar/product-catalog';
import { getV3SkuFromPlanId } from '@hotjar/product-catalog';
import { PlanColumnStyles as S } from './styles';
import { useUpgradeButtonText } from './hooks/useUpgradeButtonText';
import { usePlanColumnTag } from './hooks/usePlanColumnTag';
import { usePlanAvailability } from './hooks/usePlanAvailability';
import { PlanFeatures } from '../Upsell/components/PlanFeatures/PlanFeatures';
import { useProductPricing } from '../Upsell/hooks/useProductPricing';

interface Props<T> {
  tierProducts: T;
  tier: FeatureGroup;
  showFeaturesList: boolean;
  productType: ProductType;
  modalName: string;
  isPlanAgnostic?: boolean;
  billingPeriod: BillingPeriod;
  billingPeriodChanged: boolean;
}

export const PlanColumn = <T extends Product[]>({
  productType,
  tier,
  tierProducts,
  showFeaturesList,
  isPlanAgnostic,
  modalName,
  billingPeriod,
  billingPeriodChanged,
}: Props<T>): JSX.Element | null => {
  const { t } = useTranslation('revenue', { keyPrefix: 'plan-comparison-columns' });
  const { click, getGroupDataForSite } = useTrack();

  const { site, recommendedPlanId, referrer, featureGate, onSubmit } = useContext(
    PlanComparisonColumnsContext,
  );

  const { data: productCatalog } = useProductCatalog();

  const siteAccount = site?.account;
  const accountDto = useMemo(
    () => (siteAccount ? mapSiteAccountModelToDto(siteAccount) : undefined),
    [siteAccount],
  );

  const isRecommendedProductInCurrentTier = recommendedPlanId
    ? productIncludesSku(tierProducts, recommendedPlanId)
    : false;

  const [defaultPlan] = tierProducts;
  const recommendedProduct =
    (recommendedPlanId &&
      !isPlanAgnostic &&
      tierProducts.find(
        (product: { sku: string }): boolean => product.sku === recommendedPlanId,
      )) ||
    null;

  // Initialize selectedPlan with the default plan of the column's tier
  const [selectedPlan, setSelectedPlan] = useState<AnalyticsProduct | FeedbackProduct>(defaultPlan);

  const [showLoading, setShowLoading] = useState<boolean>(false);
  const { coveragePercentage, dailyTraffic, isCoverageMeasureComplete } = useTrafficCoverage(
    undefined,
    (selectedPlan as AnalyticsProduct).dailyCcSessions,
  );

  const isSelectedPlanSelfServable = selectedPlan.selfServeEnabled;
  const hideTrafficNumber = dailyTraffic === 0 && !isCoverageMeasureComplete;
  const { getAllowances } = useProductAllowances();
  const productAllowances = useMemo(
    () => getAllowances(selectedPlan, coveragePercentage),
    [getAllowances, selectedPlan, coveragePercentage],
  );

  const {
    isLoading: isLoadingPrice,
    price,
    symbol,
  } = useProductPricing({
    product: selectedPlan,
    site,
    billingPeriod,
  });

  const featureGroupHasMultiplePlans = tierProducts.length > 1;
  const onPlanChangeClick = (plan: AnalyticsProduct | FeedbackProduct) => {
    if (site) {
      click('MultiSku Plan selector', modalName, getGroupDataForSite(site));
    }

    setSelectedPlan(plan);
  };

  const currentPlanId = site?.effectivePlanId;
  const currentPlanSku = extractSkuFromPlanId(currentPlanId || '', productType);
  const isCurrentPlan = currentPlanSku === selectedPlan.sku;
  const { canSelfServe } = useCanSelfServeUpsell({
    site,
    selectedSkus: [getV3SkuFromPlanId(selectedPlan.sku as LegacyPlanId)],
  });

  const isAvailable = usePlanAvailability({
    featureGroup: tier,
    selectedPlan,
    recommendedPlan: recommendedProduct,
    sku: featureGate?.sku,
  });

  const isTrafficRecommendationModal = isTrafficRecommendationReferrer(referrer);

  const buttonText = useUpgradeButtonText({
    featureGroup: tier,
    selectedPlan,
    recommendedPlan: recommendedProduct,
    isRecommendedPlanInCurrentFeatureGroup: isRecommendedProductInCurrentTier,
    sku: productType,
    isTrafficRecommendationModal,
  });

  const tagText = usePlanColumnTag({
    isRecommendedPlanInCurrentFeatureGroup: isTrafficRecommendationModal
      ? recommendedPlanId === selectedPlan.sku
      : isRecommendedProductInCurrentTier,
  });

  const onUpsellClick = async () => {
    if (!site || !currentPlanId || !productCatalog || !onSubmit) {
      return;
    }
    const newTrafficCoverage =
      productType === ProductType.Analytics && (selectedPlan as AnalyticsProduct).dailyCcSessions;

    click(buttonText, modalName, getGroupDataForSite(site), {
      new_traffic_coverage: newTrafficCoverage ?? ProductType.Feedback,
    });

    if (referrer && !canSelfServe) {
      window.open(getSalesLinkByReferrer(referrer), '_blank', 'noopener noreferrer');
      return;
    }

    setShowLoading(true);

    const [analyticsSku, feedbackSku] = getProductSkusByPlanId(currentPlanId);
    const [currentAnalyticsProduct, currentFeedbackProduct] = [
      getProductBySku(analyticsSku, productCatalog),
      getProductBySku(feedbackSku, productCatalog),
    ];

    // Unlikely that we'd ever run into this, but need to check this for type-safety reasons
    if (!currentAnalyticsProduct || !currentFeedbackProduct) {
      logError(
        new Error(
          `PlanColumn: Could not get current Analytics and Feedback product by SKU. Plan ID: ${currentPlanId}`,
        ),
      );
      return;
    }

    const newProducts = {
      [ProductType.Analytics]:
        productType === ProductType.Analytics ? selectedPlan : currentAnalyticsProduct,
      [ProductType.Feedback]:
        productType === ProductType.Feedback ? selectedPlan : currentFeedbackProduct,
    };

    // This `onSubmit` callback is coming from `createGlobalUpsellModal`
    await onSubmit({
      referrer,
      selectedPlans: {
        [site.id]: newProducts,
      },
    });

    setShowLoading(false);
  };

  // If the recommendedProduct changes between renders, we should update the selected plan
  useEffect(() => {
    if (
      recommendedProduct &&
      isRecommendedProductInCurrentTier &&
      selectedPlan.sku !== recommendedProduct.sku
    ) {
      setSelectedPlan(recommendedProduct);
    }
    // We don't want to add selectedPlan to the deps array because we're updating it in the effect (infinite loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecommendedProductInCurrentTier, recommendedProduct]);

  if (!accountDto || !siteAccount) {
    return null;
  }

  const renderPriceInfoBlock = () => {
    if (isSelectedPlanSelfServable) {
      return (
        <Loader size="l" isLoading={isLoadingPrice}>
          {price && <PlanPrice symbol={symbol} price={price} billingPeriod={billingPeriod} />}
        </Loader>
      );
    }
    return <Text textType="subtitle2">{t('book-a-call')}</Text>;
  };

  return (
    <S.PlanContainer
      data-testid={`plan-comparison-${tier}-column`}
      isHighlighted={featureGate?.isPlanAgnostic ? false : isRecommendedProductInCurrentTier}
      isTrafficRecommendationModal={isTrafficRecommendationModal}
    >
      {!featureGate?.isPlanAgnostic && tagText && <S.RecommendedTag>{tagText}</S.RecommendedTag>}
      <S.Title
        data-testid={`plan-comparison-${tier}-title`}
        isAnnualPricingShown={billingPeriod === 'ANNUALLY'}
      >
        <S.FeatureGroup>{tier}</S.FeatureGroup>
        {tier === SCALE && !isSelectedPlanSelfServable ? (
          <Badge
            kind="custom"
            size="s"
            color={tokens.color.ui_disabled}
            textColor={tokens.color.text_light}
            data-testid="plan-comparison-custom-pricing"
          >
            {t('custom-pricing')}
          </Badge>
        ) : (
          renderPriceInfoBlock()
        )}
      </S.Title>

      {!isTrafficRecommendationModal && (
        <S.PlanSubTitle isAnnualPricingShown={billingPeriodChanged && billingPeriod === 'ANNUALLY'}>
          {t(`sku-subtitles.${productType}.${tier}-subtitle`)}
        </S.PlanSubTitle>
      )}
      <Stack direction="column" spacing={isTrafficRecommendationModal ? 'x3pt5' : 'x3'}>
        {featureGroupHasMultiplePlans ? (
          <div>
            <S.PlanSelectorLabel isTrafficRecommendationModal={isTrafficRecommendationModal}>
              {t('plan-selector-label')}
            </S.PlanSelectorLabel>
            <SkuDropdown
              onChange={onPlanChangeClick}
              products={tierProducts}
              activeProduct={selectedPlan}
              allowBookSalesCall={tier === 'scale'}
              productType={
                productType === ProductType.Analytics ? 'analyticsProducts' : 'feedbackProducts'
              }
              hidePlanBadges
              showTitle={false}
              siteAccount={siteAccount}
            />
            {isTrafficRecommendationModal && (
              <S.PlanTrafficCoverageLabel
                withSkuDropdown
                hideTrafficNumber={hideTrafficNumber}
                coveragePercentage={coveragePercentage}
                trafficCoverageLabel={productAllowances.trafficCoverageLabel}
                maxTrafficCoverageLabel={t('max-traffic-coverage-label')}
              />
            )}
          </div>
        ) : (
          <S.AllowanceContainer
            isTrafficRecommendationModal={isTrafficRecommendationModal}
            data-testid="plan-allowance"
          >
            {tier === SCALE && productType === ProductType.Analytics && t('unlimited-sessions')}
            {tier === SCALE && productType === ProductType.Feedback && t('unlimited-responses')}
            {tier !== SCALE && productAllowances.allowanceLabel}
            {tier !== SCALE && isTrafficRecommendationModal && (
              <S.PlanTrafficCoverageLabel
                hideTrafficNumber={hideTrafficNumber}
                coveragePercentage={coveragePercentage}
                trafficCoverageLabel={productAllowances.trafficCoverageLabel}
                maxTrafficCoverageLabel={t('max-traffic-coverage-label')}
              />
            )}
          </S.AllowanceContainer>
        )}
        <S.PlanUpgrade data-testid="cta-container">
          {isAvailable && (
            <S.StyledButton
              isTrafficRecommendationModal={isTrafficRecommendationModal}
              kind={
                isRecommendedProductInCurrentTier || (featureGate?.isPlanAgnostic && canSelfServe)
                  ? 'primary'
                  : 'secondary'
              }
              size="l"
              isLoading={showLoading}
              onClick={onUpsellClick}
              data-testid={`upsell-modals-plan-upgrade-button--${tier}`}
            >
              {buttonText}
            </S.StyledButton>
          )}

          {!isAvailable && isCurrentPlan && (
            <S.StyledButton
              size="l"
              isDisabled
              isTrafficRecommendationModal={isTrafficRecommendationModal}
              data-testid={`upsell-modals-plan-current-plan-button--${tier}`}
            >
              {buttonText}
            </S.StyledButton>
          )}

          {!isAvailable && !isCurrentPlan && (
            <S.NotAvailable isTrafficRecommendationModal={isTrafficRecommendationModal}>
              {buttonText}
            </S.NotAvailable>
          )}
        </S.PlanUpgrade>
        {showFeaturesList && (
          <PlanFeatures
            tier={tier}
            tierProducts={tierProducts}
            productType={productType}
            selectedPlan={selectedPlan}
          />
        )}
      </Stack>
    </S.PlanContainer>
  );
};
